import type { NoteSegment, UnitSystem } from './types';

type Dimension = 'volume' | 'weight' | 'length';

interface UnitDef {
  dim: Dimension;
  system: UnitSystem;
  si: number; // value in canonical SI unit (volume:ml, weight:g, length:mm)
}

// Canonical measurement units. One form per unit — authors must use these exact strings.
// Conversion factors from NIST SP 811 (https://physics.nist.gov/Pubs/SP811/appenB9.html).
// Avoirdupois weight and inch are exact under the 1959 International Yard and Pound agreement.
const UNITS: Record<string, UnitDef> = {
  tsp: { dim: 'volume', system: 'imperial', si: 4.92892159375 },
  tbsp: { dim: 'volume', system: 'imperial', si: 14.7867647813 },
  'fl oz': { dim: 'volume', system: 'imperial', si: 29.5735295625 },
  cup: { dim: 'volume', system: 'imperial', si: 236.5882365 },
  pint: { dim: 'volume', system: 'imperial', si: 473.176473 },
  quart: { dim: 'volume', system: 'imperial', si: 946.352946 },
  gallon: { dim: 'volume', system: 'imperial', si: 3785.411784 },
  ml: { dim: 'volume', system: 'metric', si: 1 },
  l: { dim: 'volume', system: 'metric', si: 1000 },
  oz: { dim: 'weight', system: 'imperial', si: 28.349523125 },
  lb: { dim: 'weight', system: 'imperial', si: 453.59237 },
  g: { dim: 'weight', system: 'metric', si: 1 },
  kg: { dim: 'weight', system: 'metric', si: 1000 },
  in: { dim: 'length', system: 'imperial', si: 25.4 },
  ft: { dim: 'length', system: 'imperial', si: 304.8 },
  mm: { dim: 'length', system: 'metric', si: 1 },
  cm: { dim: 'length', system: 'metric', si: 10 },
  m: { dim: 'length', system: 'metric', si: 1000 },
};

export function isMeasurementUnit(unit: string): boolean {
  return unit in UNITS;
}

export function unitDim(unit: string): Dimension | null {
  return UNITS[unit]?.dim ?? null;
}

/** Convert a (quantity, unit) pair to SI (ml | g | mm). Returns null when unparseable / unknown unit. */
export function toSI(quantity: string | undefined, unit: string | undefined): number | null {
  if (quantity === undefined) return null;
  const n = parseQty(quantity);
  if (n === 0) return null;
  if (!unit) return n;
  const def = UNITS[unit];
  if (!def) return null;
  return n * def.si;
}

const MIXED_FRAC_RE = /^(\d+)\s+(\d+)\/(\d+)$/;
const FRAC_RE = /^(\d+)\/(\d+)$/;
const TRAILING_ZEROS_RE = /\.?0+$/;

export function parseQty(q: string | undefined): number {
  if (!q) return 0;
  const s = q.trim();
  const mixed = s.match(MIXED_FRAC_RE);
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  const frac = s.match(FRAC_RE);
  if (frac) return Number(frac[1]) / Number(frac[2]);
  return Number(s) || 0;
}

const PRIMARY: Record<UnitSystem, Record<Dimension, string>> = {
  imperial: { volume: 'cup', weight: 'oz', length: 'in' },
  metric: { volume: 'ml', weight: 'g', length: 'cm' },
};

const UPGRADE: Record<string, { unit: string; at: number }> = {
  ml: { unit: 'l', at: 1000 },
  g: { unit: 'kg', at: 1000 },
  cm: { unit: 'm', at: 100 },
  oz: { unit: 'lb', at: 16 },
};

export function convertUnit(
  qty: number,
  unit: string,
  system: UnitSystem,
): { qty: number; unit: string } {
  const info = UNITS[unit];
  if (!info || info.system === system) return { qty, unit };
  const si = qty * info.si;
  let target = PRIMARY[system][info.dim];
  const upgrade = UPGRADE[target];
  if (upgrade) {
    const upgradedQty = si / (UNITS[upgrade.unit]?.si ?? 1);
    if (upgradedQty >= 1) target = upgrade.unit;
  }
  return { qty: si / (UNITS[target]?.si ?? 1), unit: target };
}

export function formatQty(n: number, unit?: string): string {
  if (n === 0) return '0';
  const info = unit ? UNITS[unit] : undefined;
  if (info?.system === 'metric') {
    if (n >= 10) return Math.round(n).toString();
    return (Math.round(n * 10) / 10).toString();
  }
  const whole = Math.floor(n);
  const rem = n - whole;
  for (const d of [2, 3, 4, 6, 8]) {
    const num = Math.round(rem * d);
    if (Math.abs(rem - num / d) < 0.005 && num !== 0 && num !== d) {
      return whole > 0 ? `${whole} ${num}/${d}` : `${num}/${d}`;
    }
  }
  return n.toFixed(2).replace(TRAILING_ZEROS_RE, '');
}

const TEMP_FMT = {
  F: new Intl.NumberFormat('en', { style: 'unit', unit: 'fahrenheit' }),
  C: new Intl.NumberFormat('en', { style: 'unit', unit: 'celsius' }),
} as const;

export function formatTemp(value: number, from: 'F' | 'C', system: UnitSystem): string {
  const wantF = system === 'imperial';
  if ((from === 'F') === wantF) return TEMP_FMT[from].format(value);
  const converted = wantF ? (value * 9) / 5 + 32 : ((value - 32) * 5) / 9;
  return TEMP_FMT[wantF ? 'F' : 'C'].format(Math.round(converted));
}

export function renderNote(note: string | NoteSegment[] | undefined, quantity: number): string {
  if (!note) return '';
  if (typeof note === 'string') return note;
  return note
    .map((seg) => (typeof seg === 'string' ? seg : String(Math.round(quantity * seg.multiply))))
    .join('');
}
